export enum UserConfirmationStatus {
  NotResponded = 0,
  Confirmed = 1,
  Rejected = 2,
}

export enum ReleaseStatus {
  Draft = 0,
  Released = 1,
}

export interface AllLocationStaffingResponse {
  electionId: string;
  locations: LocationStaffingResponse[];
}

export interface WorkerScheduleResponse {
  electionId: string;
  schedules: WorkerScheduleItem[];
}

export interface WorkerScheduleItem {
  id: string;
  locationId: string;
  locationName: string;
  roleId: string;
  roleName: string;
  workDate: string;
  startTime: string;
  endTime: string;
  status: number;
  userConfirmationStatus: UserConfirmationStatus;
  userConfirmationStatusReason?: string;
  party: { id: string; partyCode: string; partyName: string } | null;
  evUser: {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    cellPhone: string;
    homePhone: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  pollworker: {
    id: string;
    status: number;
    earlyVotingWorker: boolean;
    electionDayWorker: boolean;
    keyRoleId: string;
    workerLevel: number;
  };
  workHistory: {
    id: string;
    keyElectionId: string;
    keyRoleId: string;
    status: number;
    keyLocationId: string;
    workerLevel: number;
  };
  releaseStatus: ReleaseStatus;
  releasedById: string | null;
  releasedByName: string | null;
  releasedAt: string | null;
  isLegacySchedule: boolean;
}

export interface LocationStaffingResponse {
  locationId: string;
  locationName: string;
  electionId: string;
  roles: LocationStaffingRole[];
}

export interface LocationStaffingRole {
  roleId: string;
  roleName: string;
  workerCount: number;
  partyRequirements: RolePartyRequirement[];
  dates: RoleDateConfig[];
  extraAssignments: ExtraAssignment[];
}

export interface RolePartyRequirement {
  id: string;
  partyId: string;
  partyName: string;
  date: string;
  workerCount: number;
}

export interface RoleDateConfig {
  date: string;
  totalSlots: number;
  filledSlots: number;
  openSlots: number;
  partyRequirements: DatePartyRequirement[];
  slots: StaffingSlot[];
}

export interface DatePartyRequirement {
  partyId: string;
  partyName: string;
  required: number;
  filled: number;
}

export interface StaffingSlot {
  slotNumber: number;
  date: string;
  partyRequirement: SlotPartyRequirement | null;
  assignedWorker: AssignedWorker | null;
}

export interface SlotPartyRequirement {
  partyId: string;
  partyName: string;
}

export interface AssignedWorker {
  evUserId: string;
  firstName: string;
  lastName: string;
  party: string;
}

export interface ExtraAssignment {
  scheduleId: string;
  evUserId: string;
  firstName: string;
  lastName: string;
  party: string;
  reason: 'Exceeds worker count' | 'Role mismatch' | string;
}

export interface PositionUpdateRequest {
  positions: PositionUpdate[];
}

export interface PositionUpdate {
  roleId: string;
  date: string;
  workerCount: number;
  partyId: string | null;
}

export interface BulkPositionUpdateRequest {
  locations: LocationPositionUpdate[];
}

export interface LocationPositionUpdate {
  locationId: string;
  positions: PositionUpdate[];
  ensureRoleIds?: string[];
  deleteRoleIds?: string[];
}

export interface PositionUpdateResponse {
  success: boolean;
  created: number;
  updated: number;
  deleted: number;
  positions: SavedPosition[];
  errors: string[] | null;
}

export interface SavedPosition {
  id: string;
  roleId: string;
  roleName: string;
  date: string;
  workerCount: number;
  partyId: string | null;
  partyName: string | null;
}

export interface ScheduleSaveRequest {
  locations: LocationScheduleUpdate[];
}

export interface LocationScheduleUpdate {
  locationId: string;
  schedules: ScheduleItem[];
  deleteIds?: string[];
}

export interface ScheduleItem {
  id: string | null;
  evUserId: string;
  roleId: string;
  workDate: string;
  startTime: string;
  endTime: string;
  userConfirmationStatus?: UserConfirmationStatus;
  publishStatus?: ReleaseStatus;
}

export interface ScheduleSaveResponse {
  success: boolean;
  results: LocationScheduleResult[];
  errors: string[] | null;
}

export interface BulkAssignPollWorkersRequest {
  workerIds: string[];
  locationId: string;
  dates: string[];
  startTime: string;
  endTime: string;
  roleId: string;
}

export interface BulkAssignmentResponse {
  assignedCount: number;
  skippedDefaultLocationNotPresentCount: number;
  skippedNoDefaultLocationCount: number;
  skippedNoDefaultRoleCount: number;
}

export interface LocationScheduleResult {
  locationId: string;
  created: number;
  updated: number;
  deleted: number;
  schedules: SavedScheduleItem[];
}

export interface SavedScheduleItem {
  id: string;
  evUserId: string;
  roleId: string;
  workDate: string;
}

export type WorkerAvailabilityStatus = 'available' | 'unavailable' | 'no_response';

export interface WorkerAvailabilityDto {
  evUserId: string;
  firstName: string;
  lastName: string;
  status: WorkerAvailabilityStatus;
}

export interface AvailabilityDateDto {
  date: string;
  available: number;
  unavailable: number;
  noResponse: number;
  totalWorkers: number;
  workers: WorkerAvailabilityDto[];
}

export interface AvailabilityResponse {
  electionId: string;
  surveyId: string | null;
  dates: AvailabilityDateDto[];
}

export interface ReleaseSchedulesRequest {
  scheduleIds: string[] | null;
  templateId: string | null;
  skipEmail: boolean;
}

export interface ReleaseSchedulesResponse {
  success: boolean;
  releasedCount: number;
  uniqueWorkersNotified: number;
  error: string | null;
}

export interface WorkDateTrainingConflict {
  classId: string;
  evUserId: string;
  workerName: string;
  roleName: string;
  workDate: string;
  className: string;
  earliestSessionDate: string | null;
}

export interface TrainingClassSummary {
  classId: string;
  className: string;
  requirementsToCreate: number;
  availableSeats: number;
  seatShortfall: number;
  hasCapacityWarning: boolean;
  hasRemotePath: boolean;
  hasNoSessionsBeforeWorkDate: boolean;
  scheduleConflictCount: number;
  scheduleConflicts: WorkDateTrainingConflict[];
}

export interface ReleasePreviewResponse {
  scheduleCount: number;
  workerCount: number;
  workersWithEmail: number;
  workersWithoutEmail: number;
  roleTrainingPreviews: RoleTrainingPreview[];
  workerNotifications: WorkerNotificationPreview[];
  trainingClassSummaries: TrainingClassSummary[];
  validation: ReleaseValidationResult;
}

export interface RoleTrainingPreview {
  roleId: string;
  roleName: string;
  workerCount: number;
  trainingClasses: TrainingClassPreview[];
  hasNoTraining: boolean;
}

export interface TrainingClassPreview {
  classId: string;
  className: string;
  requirementsToCreate: number;
  availableSeats: number;
  seatShortfall: number;
  hasCapacityWarning: boolean;
  hasRemotePath: boolean;
  hasNoSessionsBeforeWorkDate: boolean;
}

export interface WorkerNotificationPreview {
  evUserId: string;
  workerName: string;
  hasEmail: boolean;
  scheduleCount: number;
}

export interface ReleaseValidationResult {
  canRelease: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  code: string;
  message: string;
}

export interface ValidationWarning {
  code: string;
  message: string;
  affectedCount: number;
}

export interface ConfirmationRequest {
  scheduleIds: string[];
  status: number;
  reason?: string;
}

export interface ConfirmationResponse {
  success: boolean;
  updatedCount: number;
  message?: string;
}

export interface ConfirmationSummaryResponse {
  confirmed: ConfirmationItem[];
  pending: ConfirmationItem[];
  declined: ConfirmationItem[];
  stats: ConfirmationStats;
}

export interface ConfirmationItem {
  scheduleId: string;
  evUserId: string;
  workerName: string;
  workDate: string;
  locationId: string;
  locationName: string;
  roleId: string;
  roleName: string;
  status: number;
  reason?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  hasEmail: boolean;
}

export interface ConfirmationStats {
  confirmedCount: number;
  pendingCount: number;
  declinedCount: number;
  totalReleased: number;
}

export interface SendConfirmationReminderRequest {
  workerIds: string[];
  templateId: string;
}
